import { SyntheticEvent, forwardRef, useContext, useState } from 'react';
import MuiSnackbar from '@mui/material/Snackbar';

import { Icon, Icons } from 'Icon';
import { SnackbarContext } from 'SnackbarProvider';

import {
  StyledActionText,
  StyledErrorIcon,
  StyledIconButton,
  StyledLinearProgress,
  StyledPaper,
  StyledSnackbarGrid,
  StyledTypography,
} from './SimpleSnackbar.styled';
import { SimpleSnackbarProps } from './SimpleSnackbar.types';

export const SimpleSnackbar = forwardRef<unknown, SimpleSnackbarProps>(
  (
    {
      actionText,
      anchorOrigin,
      onActionClick,
      onCloseIconClick,
      typographyProps,
      progressProps,
      // variant is aliased to snackVariant since `variant` is already an existing prop on many of the child components, such as the Button and Typography
      variant: snackVariant = 'default',
      ...props
    }: SimpleSnackbarProps,
    ref,
  ) => {
    const [open, setOpen] = useState(true);
    const { dispatch = () => {} } = useContext(SnackbarContext);
    const handleClose = (event?: SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') return;
      setOpen(false);
      if (!onCloseIconClick) {
        // we wait 200ms to allow for the leaveDuration (195ms) to finish, otherwise the snackbar disappearing animation looks janky https://mui.com/customization/transitions/#durations
        setTimeout(() => {
          dispatch({ type: 'dequeue' });
        }, 200);
      }
      onCloseIconClick?.();
    };

    let autoHideDuration: number | null = 4000;
    if (props.autoHideDuration === undefined && actionText) autoHideDuration = 8000;
    if (props.autoHideDuration !== undefined) autoHideDuration = props.autoHideDuration;

    let startAdornment = props.startAdornment;
    if (snackVariant === 'error') {
      startAdornment = (
        <StyledErrorIcon data-testid="snackbar-error-icon" icon={Icons.removeSolid} size={16} />
      );
    }

    return (
      <MuiSnackbar
        {...props}
        anchorOrigin={anchorOrigin}
        autoHideDuration={autoHideDuration}
        onClose={handleClose}
        open={open}
        ref={ref}
      >
        <StyledPaper snackVariant={snackVariant} {...props.paperProps}>
          <StyledSnackbarGrid container role="alert">
            {startAdornment}
            <StyledTypography {...typographyProps} snackVariant={snackVariant} variant="body2">
              {props.message}
              {snackVariant === 'progress' && <StyledLinearProgress {...progressProps} />}
            </StyledTypography>
            {actionText && (
              <StyledActionText
                disableRipple
                onClick={onActionClick}
                snackVariant={snackVariant}
                variant="text"
              >
                {actionText}
              </StyledActionText>
            )}
            <StyledIconButton aria-label="close alert" onClick={handleClose} snackVariant={snackVariant}>
              <Icon icon={Icons.close} />
            </StyledIconButton>
          </StyledSnackbarGrid>
        </StyledPaper>
      </MuiSnackbar>
    );
  },
);
SimpleSnackbar.displayName = 'SimpleSnackbar';
